/**
 * Reception data layer — typed query/mutation hooks over @/api. Every key is lab-scoped via
 * qk.scoped(labId, "reception", …) so a lab switch never bleeds cache (multi-tenant). Mutations
 * declare the keys they invalidate (CRUD-complete / supersede-by-reissue, L-1).
 *
 * Endpoints are verbatim from server/src/modules/reception/routes.js (mounted under /api/v1).
 */
import { useApiMutation, useApiQuery, api, qk } from "@/api";
import { useAuthStore } from "@/auth";
import type {
  BillResult,
  CreateDemandeBody,
  Demande,
  DossierPatient,
  EnqueueResult,
  Id,
  MintLienResult,
  PayBody,
  PayResult,
  PlanTubesResult,
  RegisterDossierBody,
  RegisterDossierResult,
  ScanResult,
  SuggestNinResult,
  Patient,
  LienCanal,
} from "./types";

/** The current laboratoire id for lab-scoped keys (auth store is the single source). */
export function useLabId(): Id | null {
  return useAuthStore((s) => s.lab?.id ?? s.user?.laboratoireId ?? null);
}

// ── Dossiers / identity ────────────────────────────────────────────────────────

/** Lab-local typeahead search (nom/prenom/telephone/nin). Disabled until `q` is non-empty. */
export function useDossierSearch(q: string) {
  const labId = useLabId();
  const trimmed = q.trim();
  return useApiQuery<Patient[]>(
    qk.scoped(labId, "reception", "dossier", "search", trimmed),
    "/reception/dossiers",
    { query: { q: trimmed }, enabled: trimmed.length >= 2, staleTime: 10_000 },
  );
}

/** A single dossier (with patient). */
export function useDossier(id: Id | null) {
  const labId = useLabId();
  return useApiQuery<DossierPatient>(
    qk.scoped(labId, "reception", "dossier", "detail", id ?? "_"),
    `/reception/dossiers/${id}`,
    { enabled: id != null },
  );
}

export function useRegisterDossier() {
  const labId = useLabId();
  return useApiMutation<RegisterDossierResult, RegisterDossierBody>(
    (body) => api.post<RegisterDossierResult>("/reception/dossiers", body),
    { invalidate: [qk.scoped(labId, "reception", "dossier")] },
  );
}

/** Cross-lab NIN suggestion (suggest-then-confirm, R-11). Imperative so the form drives it on blur. */
export function suggestByNin(nin: string): Promise<SuggestNinResult> {
  return api.post<SuggestNinResult>("/reception/dossiers/suggest-nin", { nin });
}

// ── Rx-QR scan (EB-01 — dark behind the adapter day-1) ──────────────────────────

export function useScanOrdonnance() {
  return useApiMutation<ScanResult, { raw_token_or_code: string }>((body) =>
    api.post<ScanResult>("/reception/scan", body),
  );
}

// ── Demandes / orders ───────────────────────────────────────────────────────────

export function useDemande(id: Id | null) {
  const labId = useLabId();
  return useApiQuery<Demande>(
    qk.scoped(labId, "reception", "demande", "detail", id ?? "_"),
    `/reception/demandes/${id}`,
    { enabled: id != null },
  );
}

export function useCreateDemande() {
  const labId = useLabId();
  return useApiMutation<Demande, CreateDemandeBody>(
    (body) => api.post<Demande>("/reception/demandes", body),
    { invalidate: [qk.scoped(labId, "reception", "demande")] },
  );
}

function demandeKey(labId: Id | null, id: Id) {
  return qk.scoped(labId, "reception", "demande", "detail", id);
}

/** Issue the facture + open the demande (billing handoff). */
export function useBillDemande(id: Id) {
  const labId = useLabId();
  return useApiMutation<BillResult, void>(
    () => api.post<BillResult>(`/reception/demandes/${id}/facturer`),
    { invalidate: [demandeKey(labId, id)] },
  );
}

/** Collect cash (behind the open-caisse gate + finance.paiement.collect). */
export function usePayDemande(id: Id) {
  const labId = useLabId();
  return useApiMutation<PayResult, PayBody>(
    (body) => api.post<PayResult>(`/reception/demandes/${id}/payer`, body),
    { invalidate: [demandeKey(labId, id)] },
  );
}

/** Generate tubes + their hybrid-QR vignettes (raw token returned ONCE — print immediately). */
export function usePlanTubes(id: Id) {
  const labId = useLabId();
  return useApiMutation<PlanTubesResult, { nature_by_type_tube?: Record<string, string> }>(
    (body) => api.post<PlanTubesResult>(`/reception/demandes/${id}/tubes`, body),
    { invalidate: [demandeKey(labId, id)] },
  );
}

/** ONE prélèvement ticket per patient → the sampling queue. */
export function useEnqueueDemande(id: Id) {
  const labId = useLabId();
  return useApiMutation<EnqueueResult, void>(
    () => api.post<EnqueueResult>(`/reception/demandes/${id}/enfiler`),
    { invalidate: [demandeKey(labId, id)] },
  );
}

/** Soft-cancel the demande (annulee). */
export function useCancelDemande(id: Id) {
  const labId = useLabId();
  return useApiMutation<Demande, void>(
    () => api.post<Demande>(`/reception/demandes/${id}/annuler`),
    { invalidate: [demandeKey(labId, id), qk.scoped(labId, "reception", "demande")] },
  );
}

// ── ClaimCode lien (R-09) ────────────────────────────────────────────────────────

export function useMintLien() {
  return useApiMutation<MintLienResult, { dossier_id: Id; canal?: LienCanal }>((body) =>
    api.post<MintLienResult>("/reception/liens", body),
  );
}
